"use client";

import React, {
  useRef,
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import {
  getLocalStorage,
  setLocalStorage,
  STORAGE_KEYS,
} from "@repo/utils/storage";
import { useSmallViewport } from "../viewport";

type GridLayoutContextProps = {
  columns: number;
  setColumns: (columns: number) => void;
};

type GridLayoutProviderProps = {
  children: ReactNode;
  defaultColumns: number;
};

const GridLayoutContext = createContext<GridLayoutContextProps>({
  columns: 2,
  setColumns: () => {},
});

const setStorage = (columns: number, key: string) => {
  const gridLocalStorage = getLocalStorage(STORAGE_KEYS.PLP_GRID_LAYOUT) || {};

  setLocalStorage(STORAGE_KEYS.PLP_GRID_LAYOUT, {
    ...gridLocalStorage,
    [key]: {
      columns,
    },
  });
};

const getKey = (isMobile: boolean) => {
  return isMobile ? "mobile" : "desktop";
};

export const GridLayoutProvider = ({
  children,
  defaultColumns,
}: GridLayoutProviderProps) => {
  const [columns, setColumns] = useState<number | null>(null);
  const isMobile = useSmallViewport();
  const key = useRef(getKey(isMobile));

  useEffect(() => {
    if (!columns) return;
    setStorage(columns, key.current);
  }, [columns, key]);

  useEffect(() => {
    const gridLocalStorage = (getLocalStorage(STORAGE_KEYS.PLP_GRID_LAYOUT) ||
      {}) as { [key: string]: { columns: number } };
    key.current = getKey(isMobile);
    const viewportStorageObject = gridLocalStorage[key.current];

    setColumns(viewportStorageObject?.columns || null);
  }, [isMobile, defaultColumns]);

  const providedColumns = columns || defaultColumns;

  return (
    <GridLayoutContext.Provider
      value={{ columns: providedColumns, setColumns }}
    >
      {children}
    </GridLayoutContext.Provider>
  );
};

export const useGridLayout = (): GridLayoutContextProps =>
  useContext(GridLayoutContext);
